import { z } from 'zod';

// =====================
// Meeting Recording Enums
// =====================

export enum MeetingRecordingSourceEnum {
  DESKTOP_CAPTURE = 'DESKTOP_CAPTURE',
  BOT = 'BOT',
}

export enum MeetingRecordingStatusEnum {
  UPLOADING = 'UPLOADING',
  UPLOADED = 'UPLOADED',
  TRANSCRIBING = 'TRANSCRIBING',
  SUMMARIZING = 'SUMMARIZING',
  READY = 'READY',
  FAILED = 'FAILED',
}

export enum MeetingRecordingErrorStageEnum {
  UPLOAD = 'UPLOAD',
  TRANSCRIBE = 'TRANSCRIBE',
  SUMMARIZE = 'SUMMARIZE',
}

// =====================
// Zod Schemas
// =====================

export const MeetingRecordingSourceSchema = z.nativeEnum(MeetingRecordingSourceEnum);
export const MeetingRecordingStatusSchema = z.nativeEnum(MeetingRecordingStatusEnum);

export const TranscriptSegmentSchema = z.object({
  start: z.number(), // seconds
  end: z.number(),
  text: z.string(),
  speaker: z.string().optional().nullable(),
});

export const MeetingRecordingSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  createdBy: z.string().uuid(),
  source: MeetingRecordingSourceSchema.default(MeetingRecordingSourceEnum.DESKTOP_CAPTURE),
  status: MeetingRecordingStatusSchema.default(MeetingRecordingStatusEnum.UPLOADING),
  title: z.string().max(500).optional().nullable(),
  audioKey: z.string().optional().nullable(),
  documentId: z.string().uuid().optional().nullable(),
  mimeType: z.string().max(100).optional().nullable(),
  fileSize: z.number().optional().nullable(), // BigInt serialized as number
  durationSeconds: z.number().optional().nullable(),
  recordedAt: z.date().optional().nullable(),
  eventId: z.string().uuid().optional().nullable(),
  noteId: z.string().uuid().optional().nullable(),
  moduleType: z.string().optional().nullable(),
  moduleId: z.string().uuid().optional().nullable(),
  errorMessage: z.string().optional().nullable(),
  errorStage: z.nativeEnum(MeetingRecordingErrorStageEnum).optional().nullable(),
  attemptCount: z.number().default(0),
  processingStartedAt: z.date().optional().nullable(),
  completedAt: z.date().optional().nullable(),
  metadata: z.record(z.any()).default({}),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().optional().nullable(),
});

export const MeetingTranscriptSchema = z.object({
  id: z.string().uuid(),
  recordingId: z.string().uuid(),
  engine: z.string().max(50),
  language: z.string().max(10).optional().nullable(),
  fullText: z.string(),
  segments: z.array(TranscriptSegmentSchema).default([]),
  // Raw diarization label → display name ("spk_0" → "Joel Thomas");
  // AI-inferred during summarization, editable by the user afterwards
  speakers: z.record(z.string()).default({}),
  wordCount: z.number().optional().nullable(),
  createdAt: z.date(),
});

// =====================
// Create/Update DTOs
// =====================

export const CreateMeetingRecordingSchema = z.object({
  fileName: z.string().min(1).max(255),
  mimeType: z
    .string()
    .max(100)
    .regex(/^audio\//, 'Only audio files are accepted'),
  fileSize: z.number().int().positive(),
  durationSeconds: z.number().int().positive().optional(),
  recordedAt: z.coerce.date().optional(),
  title: z.string().max(500).optional(),
  eventId: z.string().uuid().optional(),
  moduleType: z.string().optional(),
  moduleId: z.string().uuid().optional(),
  metadata: z.record(z.any()).optional(),
});

export const MeetingRecordingPartUrlsSchema = z.object({
  partNumbers: z.array(z.number().int().min(1).max(10000)).min(1).max(100),
});

export const CompleteMeetingRecordingSchema = z.object({
  parts: z
    .array(
      z.object({
        partNumber: z.number().int().min(1).max(10000),
        etag: z.string().min(1),
      }),
    )
    .min(1),
  durationSeconds: z.number().int().positive().optional(),
});

export const UpdateMeetingRecordingSchema = z.object({
  title: z.string().max(500).optional(),
  moduleType: z.string().optional().nullable(),
  moduleId: z.string().uuid().optional().nullable(),
  eventId: z.string().uuid().optional().nullable(),
});

export const UpdateMeetingSpeakersSchema = z.object({
  // Full replacement map: raw label → display name
  speakers: z.record(z.string().min(1).max(200)),
});

// =====================
// API Response Types
// =====================

export const CreateMeetingRecordingResponseSchema = z.object({
  recordingId: z.string().uuid(),
  key: z.string(),
  uploadId: z.string(),
  partSize: z.number(),
});

export const MeetingRecordingAudioUrlSchema = z.object({
  url: z.string(),
  mimeType: z.string().nullable(),
  durationSeconds: z.number().nullable(),
  expiresAt: z.string(), // ISO timestamp; refetch after expiry
});

// =====================
// Inferred Types
// =====================

export type MeetingRecordingType = z.infer<typeof MeetingRecordingSchema>;
export type MeetingTranscriptType = z.infer<typeof MeetingTranscriptSchema>;
export type TranscriptSegmentType = z.infer<typeof TranscriptSegmentSchema>;
export type CreateMeetingRecordingType = z.infer<typeof CreateMeetingRecordingSchema>;
export type MeetingRecordingPartUrlsType = z.infer<typeof MeetingRecordingPartUrlsSchema>;
export type CompleteMeetingRecordingType = z.infer<typeof CompleteMeetingRecordingSchema>;
export type UpdateMeetingRecordingType = z.infer<typeof UpdateMeetingRecordingSchema>;
export type UpdateMeetingSpeakersType = z.infer<typeof UpdateMeetingSpeakersSchema>;
export type CreateMeetingRecordingResponseType = z.infer<typeof CreateMeetingRecordingResponseSchema>;
export type MeetingRecordingAudioUrlType = z.infer<typeof MeetingRecordingAudioUrlSchema>;

// =====================
// Summary templates
// =====================

/** Reference stored on a recording (`metadata.summaryTemplate`):
 *  a built-in key ('sales_call') or an org custom template ('custom:<uuid>'). */
export const CUSTOM_TEMPLATE_PREFIX = 'custom:';

export interface MeetingSummaryTemplateDef {
  key: string;
  name: string;
  description: string;
  /** Appended to the summarizer prompt to shape the notes for this meeting type */
  instructions: string;
}

export const MEETING_SUMMARY_TEMPLATES: MeetingSummaryTemplateDef[] = [
  {
    key: 'sales_call',
    name: 'Sales call',
    description: 'Prospect needs, objections, commercials, next steps',
    instructions:
      'This is a sales call. Structure summary_sections as: "Prospect & context" (company, attendees, how the call came about), ' +
      '"Needs & pain points", "Product discussion & objections" (what resonated, every objection and how it was answered), ' +
      '"Commercials" (budget, pricing discussed, timeline, decision process/makers — note explicitly when not discussed), ' +
      'and "Next steps". Always capture competitor mentions verbatim. Action items should focus on follow-ups owed to the prospect.',
  },
  {
    key: 'standup',
    name: 'Standup',
    description: 'Per-person progress, plans, and blockers',
    instructions:
      'This is a team standup. Keep the summary tight. Structure summary_sections as one section per participant with three bullets ' +
      '(Done / Next / Blockers), followed by a "Blockers & risks" section aggregating anything that needs escalation. ' +
      'Action items should be blockers needing follow-up, not routine planned work.',
  },
  {
    key: 'one_on_one',
    name: '1:1',
    description: 'Topics, feedback both ways, growth, commitments',
    instructions:
      'This is a 1:1 conversation. Structure summary_sections as: "Topics discussed", "Feedback" (in both directions, attributed), ' +
      '"Growth & career" (skip if not discussed), and "Commitments" (who agreed to what). Keep a neutral, private tone — ' +
      'this summary is personal working notes, not a broadcast. Do not editorialize on sensitive topics.',
  },
  {
    key: 'interview',
    name: 'Interview',
    description: 'Candidate background, signals, concerns, recommendation inputs',
    instructions:
      'This is a job interview. Structure summary_sections as: "Candidate background" (experience relevant to the role), ' +
      '"Strengths & signals" (with the concrete evidence/answers behind each), "Concerns & gaps" (only what the conversation supports), ' +
      'and "Candidate questions" (what they asked — it signals what they care about). Quote key answers rather than paraphrasing loosely. ' +
      'Do NOT output a hire/no-hire recommendation — capture evidence, not verdicts. Action items are process follow-ups (references, next rounds, feedback to submit).',
  },
];

export function getBuiltinTemplate(
  key: string,
): MeetingSummaryTemplateDef | undefined {
  return MEETING_SUMMARY_TEMPLATES.find((t) => t.key === key);
}

// Org custom template (API shape)
export const MeetingSummaryTemplateSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  createdBy: z.string().uuid().optional().nullable(),
  name: z.string().max(100),
  instructions: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const CreateMeetingSummaryTemplateSchema = z.object({
  name: z.string().min(1).max(100),
  instructions: z.string().min(1).max(4000),
});

export const MeetingTemplatePreferenceSchema = z.object({
  eventKey: z.string().max(255),
  templateRef: z.string().max(120).nullable(), // null clears the default
});

export type MeetingSummaryTemplateType = z.infer<typeof MeetingSummaryTemplateSchema>;
export type CreateMeetingSummaryTemplateType = z.infer<typeof CreateMeetingSummaryTemplateSchema>;
export type MeetingTemplatePreferenceType = z.infer<typeof MeetingTemplatePreferenceSchema>;

// Structured output of the AI summarizer step.
// Markdown fields may contain [[t:HH:MM:SS]] citation markers pointing at the
// supporting transcript moment; the note builder converts them to links.
export interface MeetingSummaryResult {
  title: string;
  summarySections: { heading: string; markdown: string }[];
  decisions: string[];
  actionItems: { description: string; assigneeHint?: string | null; dueDateHint?: string | null }[];
  // Diarization label → inferred participant name (only labels the model
  // could confidently identify from the conversation/attendee hints)
  speakers?: Record<string, string>;
}
